// Bolo3K mobile lobby — full-screen, touch-first replacement for the
// scaffold's temporary join form. Rendered into #lobby-root (z 40, owns
// pointer events, hides via :empty when cleared).
//
// Navigation contract (same as the temp flow in main.ts): joining a battle
// means setting `?room=<id>&name=<callsign>` and reloading via
// location.search. The game bootstrap then connects from the URL params.
//
// Bots: the desktop client adds bots POST-join — after the welcome lands,
// main.ts sends one c2s.addBot per queued personality via Connection.addBot
// (see packages/client/src/main.ts, the onWelcome handler). Because the
// mobile join flow is a page reload, the chosen bots are stashed in
// sessionStorage under PENDING_BOTS_STORAGE_KEY for the game bootstrap to
// send after its own welcome (integration point for Wave 3 — this module
// cannot touch main.ts).

import {
  BOT_PERSONALITIES,
  GameType,
  MAX_NAME_LENGTH,
  type BotPersonality,
  type GameTypeCode,
  type LobbyResponse,
  type LobbyRoomEntry,
} from '@bolo2k/shared';
import { createRoom, fetchLobby } from './lobbyApi.js';
import './mobileLobby.css';

/** localStorage key for the persisted callsign (prefilled on return visits). */
export const CALLSIGN_STORAGE_KEY = 'bolo3k.callsign';

/** sessionStorage key holding a JSON array of BotPersonality strings chosen
 *  in the create-battle form. Written immediately before the join reload;
 *  main.ts should read + delete it after its welcome lands and send one
 *  Connection.addBot(personality) per entry (mirroring the desktop flow).
 *  sessionStorage scoping means a fresh tab / PWA relaunch never replays
 *  stale bots, and main.ts deleting the key keeps a manual mid-game reload
 *  from re-adding them. */
export const PENDING_BOTS_STORAGE_KEY = 'bolo3k.pendingBots';

/** Same cadence as the desktop lobby (client/src/lobby.ts LOBBY_POLL_MS). */
const LOBBY_POLL_MS = 2_000;

const GAME_TYPE_LABELS: Record<GameTypeCode, string> = {
  [GameType.Open]: 'Open',
  [GameType.Tournament]: 'Tournament',
  [GameType.StrictTournament]: 'Strict Tournament',
};

const GAME_TYPE_HINTS: Record<GameTypeCode, string> = {
  [GameType.Open]: 'Full loadout on every respawn.',
  [GameType.Tournament]: 'Respawn shells scale with neutral bases; no mines/trees.',
  [GameType.StrictTournament]: 'Bare-tank respawn — drive to a base for everything.',
};

const PERSONALITY_LABELS: Record<BotPersonality, string> = {
  default: 'Default (Balanced)',
  assassin: 'Assassin',
  defensive: 'Defensive',
  pillhunter: 'Pill Hunter',
  basehunter: 'Base Hunter',
};

// Compact spin on the desktop lobby's Rank+Noun CamelCase callsign
// generator (client/src/lobby.ts randomCallsign) — enough combos that an
// 8-player room virtually never collides, small enough not to bloat the
// mobile bundle. Longest combo ("CommanderVengeance", 18) fits well inside
// MAX_NAME_LENGTH (24).
const CALLSIGN_RANKS = [
  'Sergeant', 'Captain', 'Major', 'Colonel', 'Admiral', 'Commander',
  'Chief', 'Corsair',
];
const CALLSIGN_NOUNS = [
  'Wolf', 'Hawk', 'Falcon', 'Cobra', 'Raven', 'Phoenix',
  'Storm', 'Thunder', 'Blaze', 'Iron',
  'Maverick', 'Phantom', 'Wraith', 'Ranger', 'Ghost', 'Striker',
  'Fury', 'Vengeance', 'Kraken', 'Bones',
];

function randomCallsign(): string {
  const r = CALLSIGN_RANKS[Math.floor(Math.random() * CALLSIGN_RANKS.length)] ?? 'Captain';
  const n = CALLSIGN_NOUNS[Math.floor(Math.random() * CALLSIGN_NOUNS.length)] ?? 'Wolf';
  return `${r}${n}`;
}

function loadStoredCallsign(): string {
  try {
    return localStorage.getItem(CALLSIGN_STORAGE_KEY) ?? '';
  } catch {
    return ''; // storage unavailable (private mode, etc.)
  }
}

function saveStoredCallsign(name: string): void {
  try {
    if (name) localStorage.setItem(CALLSIGN_STORAGE_KEY, name);
    else localStorage.removeItem(CALLSIGN_STORAGE_KEY);
  } catch {
    // Best-effort only.
  }
}

function escapeHTML(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const escapeAttr = escapeHTML;

// ---------------------------------------------------------------------------
// Entry point.
// ---------------------------------------------------------------------------

export function initMobileLobby(root: HTMLElement): void {
  const overlay = document.createElement('div');
  overlay.className = 'ml-lobby';
  overlay.innerHTML = renderSkeleton();
  root.appendChild(overlay);

  const $ = <T extends Element = HTMLElement>(sel: string): T => {
    const el = overlay.querySelector<T>(sel);
    if (!el) throw new Error(`mobile lobby element not found: ${sel}`);
    return el;
  };

  const callsignInput = $<HTMLInputElement>('[data-callsign]');
  const callsignWarnEl = $<HTMLElement>('[data-callsign-warn]');
  const playersEl = $<HTMLElement>('[data-players]');
  const battlesEl = $<HTMLElement>('[data-battles]');
  const roomsEl = $<HTMLElement>('[data-rooms]');
  const battlesStatusEl = $<HTMLElement>('[data-battles-status]');
  const battlesView = $<HTMLElement>('[data-view="battles"]');
  const createView = $<HTMLElement>('[data-view="create"]');
  const newBattleBtn = $<HTMLButtonElement>('[data-new-battle]');
  const backBtn = $<HTMLButtonElement>('[data-back]');
  const mapFilterInput = $<HTMLInputElement>('[data-map-filter]');
  const mapsEl = $<HTMLElement>('[data-maps]');
  const modesEl = $<HTMLElement>('[data-modes]');
  const modeHintEl = $<HTMLElement>('[data-mode-hint]');
  const minesToggle = $<HTMLButtonElement>('[data-hidden-mines]');
  const botsEl = $<HTMLElement>('[data-bots]');
  const botsSummaryEl = $<HTMLElement>('[data-bots-summary]');
  const createBtn = $<HTMLButtonElement>('[data-create]');
  const createStatusEl = $<HTMLElement>('[data-create-status]');
  const scrollEl = $<HTMLElement>('.ml-scroll');

  // ---- State --------------------------------------------------------------
  let latestLobby: LobbyResponse | null = null;
  let lobbyError: string | null = null;
  let selectedMap = ''; // set from the first successful lobby fetch
  let gameType: GameTypeCode = GameType.Open;
  let hiddenMines = true; // desktop default (checkbox ships checked)
  const botCounts = new Map<BotPersonality, number>();
  let busy = false; // a join/create is in flight — ignore further taps
  let renderedMapKey = ''; // guards against re-rendering the map list mid-scroll
  let disposed = false;

  callsignInput.maxLength = MAX_NAME_LENGTH;
  callsignInput.value = loadStoredCallsign() || randomCallsign();

  const currentName = (): string =>
    callsignInput.value.trim().slice(0, MAX_NAME_LENGTH);

  // ---- Polling lifecycle ----------------------------------------------------
  // Poll /api/lobby every LOBBY_POLL_MS while the lobby is visible. Paused
  // when the tab is hidden; torn down entirely when the overlay leaves the
  // DOM (MutationObserver on the root) or when we navigate into a battle.
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const refresh = async (): Promise<void> => {
    try {
      latestLobby = await fetchLobby();
      lobbyError = null;
    } catch (err) {
      // Keep the last good data if we have any (transient poll failures
      // shouldn't blank the list — same posture as the desktop lobby).
      if (!latestLobby) lobbyError = String(err);
    }
    if (!disposed) renderData();
  };

  const startPolling = (): void => {
    if (pollTimer !== null || disposed) return;
    pollTimer = setInterval(() => void refresh(), LOBBY_POLL_MS);
  };
  const stopPolling = (): void => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const onVisibility = (): void => {
    if (document.hidden) stopPolling();
    else if (!disposed) {
      void refresh(); // immediate catch-up, then resume cadence
      startPolling();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    stopPolling();
    document.removeEventListener('visibilitychange', onVisibility);
    rootObserver.disconnect();
  };

  // If something external empties #lobby-root (the :empty hide contract),
  // tear down the interval + listeners rather than leaking them.
  const rootObserver = new MutationObserver(() => {
    if (!overlay.isConnected) dispose();
  });
  rootObserver.observe(root, { childList: true });

  // ---- Navigation (the join contract) --------------------------------------
  // Same flow as the scaffold's temp form: set ?room=<id>&name=<callsign>
  // and reload via location.search (name omitted when blank, matching
  // main.ts which defaults it to '').
  const navigateToBattle = (roomId: string, bots: BotPersonality[]): void => {
    const name = currentName();
    saveStoredCallsign(name);
    try {
      if (bots.length > 0) sessionStorage.setItem(PENDING_BOTS_STORAGE_KEY, JSON.stringify(bots));
      else sessionStorage.removeItem(PENDING_BOTS_STORAGE_KEY);
    } catch {
      // sessionStorage unavailable — bots are a nicety, the join still works.
    }
    dispose();
    const next = new URLSearchParams();
    next.set('room', roomId);
    if (name) next.set('name', name);
    location.search = next.toString();
  };

  // ---- Rendering ------------------------------------------------------------

  const setBattlesStatus = (msg: string, isError = false): void => {
    battlesStatusEl.textContent = msg;
    battlesStatusEl.classList.toggle('error', isError);
  };

  const setCreateStatus = (msg: string, isError = false): void => {
    createStatusEl.textContent = msg;
    createStatusEl.classList.toggle('error', isError);
  };

  const renderData = (): void => {
    if (lobbyError !== null && !latestLobby) {
      roomsEl.innerHTML = `
        <div class="ml-error">
          <div>// lobby unreachable — ${escapeHTML(lobbyError)}</div>
          <button class="ml-btn" type="button" data-retry>Retry</button>
        </div>`;
      playersEl.textContent = '–';
      battlesEl.textContent = '–';
      return;
    }
    if (!latestLobby) return; // first fetch still in flight — keep "loading"

    const rooms = latestLobby.rooms;
    playersEl.textContent = String(rooms.reduce((sum, r) => sum + r.players, 0));
    battlesEl.textContent = String(rooms.length);
    renderRooms(rooms);
    renderMaps(latestLobby.maps);
    refreshCallsignWarn();
  };

  const renderRooms = (rooms: LobbyRoomEntry[]): void => {
    if (rooms.length === 0) {
      roomsEl.innerHTML =
        '<div class="ml-empty">// no active battles — start a new one below</div>';
      return;
    }
    const myName = currentName();
    roomsEl.innerHTML = rooms
      .map((r) => {
        const full = r.players >= r.capacity;
        // Same display rule as desktop: room name doubles as the title;
        // show the map as a separate badge only when it differs.
        const mapBadge = r.name === r.mapName
          ? ''
          : `<span class="ml-badge map">${escapeHTML(r.mapName)}</span>`;
        const typeLabel = GAME_TYPE_LABELS[r.gameType as GameTypeCode] ?? `mode ${r.gameType}`;
        const minesBadge = r.hiddenMines
          ? '<span class="ml-badge mines">Hidden Mines</span>'
          : '';
        const fullBadge = full ? '<span class="ml-badge fullflag">Full</span>' : '';
        // Collision advisory mirrors desktop: active seated callsigns only;
        // the server auto-suffixes on hello as the definitive fallback.
        const warn = myName.length > 0 && r.callsigns.includes(myName)
          ? '<div class="ml-room-warn">// callsign in use — auto-suffix on join</div>'
          : '';
        return `
          <button class="ml-room" type="button" data-room-id="${escapeAttr(r.id)}"${full ? ' disabled' : ''}>
            <div class="ml-room-top">
              <span class="ml-room-name">${escapeHTML(r.name)}</span>
              <span class="ml-room-pop${full ? ' full' : ''}">${r.players} / ${r.capacity}</span>
            </div>
            <div class="ml-room-badges">
              ${mapBadge}
              <span class="ml-badge">${escapeHTML(typeLabel)}</span>
              ${minesBadge}
              ${fullBadge}
            </div>
            ${warn}
          </button>`;
      })
      .join('');
  };

  const refreshCallsignWarn = (): void => {
    const myName = currentName();
    const collides = !!latestLobby && myName.length > 0 &&
      latestLobby.rooms.some((r) => r.callsigns.includes(myName));
    callsignWarnEl.hidden = !collides;
  };

  const renderMaps = (maps: LobbyResponse['maps']): void => {
    // Default map: prefer Everard Island (canonical first map, same rule as
    // desktop), else the first in the catalogue.
    if (!selectedMap) {
      selectedMap = maps.find((m) => /everard/i.test(m.name))?.name
        ?? maps[0]?.name
        ?? '';
    }
    const filter = mapFilterInput.value.trim().toLowerCase();
    const visible = filter
      ? maps.filter((m) => m.name.toLowerCase().includes(filter))
      : maps;
    // Skip DOM churn unless the visible list or selection actually changed —
    // a 2 s innerHTML swap would reset the picker's scroll position.
    const key = `${selectedMap} ${filter} ${visible.map((m) => m.name).join(' ')}`;
    if (key === renderedMapKey) return;
    renderedMapKey = key;
    if (visible.length === 0) {
      mapsEl.innerHTML = '<div class="ml-empty">// no maps match</div>';
      return;
    }
    mapsEl.innerHTML = visible
      .map((m) => `
        <button class="ml-map" type="button" data-map-name="${escapeAttr(m.name)}"
                aria-pressed="${m.name === selectedMap}">
          <span>${escapeHTML(m.name)}</span>
          <span class="starts">${m.starts} starts</span>
        </button>`)
      .join('');
  };

  const totalBots = (): number => {
    let n = 0;
    for (const c of botCounts.values()) n += c;
    return n;
  };

  const botList = (): BotPersonality[] => {
    const out: BotPersonality[] = [];
    for (const p of BOT_PERSONALITIES) {
      const c = botCounts.get(p) ?? 0;
      for (let i = 0; i < c; i++) out.push(p);
    }
    return out;
  };

  const renderBots = (): void => {
    botsEl.innerHTML = BOT_PERSONALITIES
      .map((p) => {
        const count = botCounts.get(p) ?? 0;
        return `
          <div class="ml-bot${count > 0 ? ' picked' : ''}">
            <button class="ml-bot-add" type="button" data-bot-add="${p}">
              <span>+ ${escapeHTML(PERSONALITY_LABELS[p])}</span>
              <span class="count">${count > 0 ? `×${count}` : ''}</span>
            </button>
            <button class="ml-bot-minus" type="button" data-bot-minus="${p}"
                    aria-label="remove one ${escapeAttr(PERSONALITY_LABELS[p])} bot"
                    ${count === 0 ? 'disabled' : ''}>−</button>
          </div>`;
      })
      .join('');
    const n = totalBots();
    botsSummaryEl.textContent = n === 0
      ? '// none — pure human game'
      : `// ${n} bot${n === 1 ? '' : 's'} will deploy after you join`;
  };

  const renderModes = (): void => {
    const types: GameTypeCode[] = [GameType.Open, GameType.Tournament, GameType.StrictTournament];
    modesEl.innerHTML = types
      .map((g) => `
        <button class="ml-mode" type="button" data-game-type="${g}" aria-pressed="${g === gameType}">
          <span class="dot"></span><span>${escapeHTML(GAME_TYPE_LABELS[g])}</span>
        </button>`)
      .join('');
    modeHintEl.textContent = GAME_TYPE_HINTS[gameType] ?? '';
  };

  // ---- Interactions ----------------------------------------------------------

  // Battles list: tap a card to join; retry button lives in the same container.
  roomsEl.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    if (target.closest('[data-retry]')) {
      roomsEl.innerHTML = '<div class="ml-loading">// contacting HQ…</div>';
      lobbyError = null;
      void refresh();
      return;
    }
    const card = target.closest<HTMLButtonElement>('.ml-room[data-room-id]');
    if (!card || card.disabled) return;
    const roomId = card.dataset.roomId;
    if (roomId) void joinRoom(roomId);
  });

  // Join = re-verify against a fresh /api/lobby first (the reload contract
  // means post-navigation failures are invisible until the game screen, so
  // catch what we can here: rooms that just filled or ended).
  async function joinRoom(roomId: string): Promise<void> {
    if (busy) return;
    busy = true;
    setBattlesStatus('// checking battle…');
    try {
      const fresh = await fetchLobby();
      latestLobby = fresh;
      lobbyError = null;
      renderData();
      const room = fresh.rooms.find((r) => r.id === roomId);
      if (!room) {
        setBattlesStatus('// that battle just ended — list refreshed', true);
        return;
      }
      if (room.players >= room.capacity) {
        setBattlesStatus('// battle just filled up — pick another', true);
        return;
      }
      setBattlesStatus(`// deploying to ${room.name}…`);
      navigateToBattle(roomId, []);
    } catch (err) {
      setBattlesStatus(`// join failed: ${String(err)}`, true);
    } finally {
      busy = false;
    }
  }

  // Callsign: persist on change; live-refresh the collision advisories.
  callsignInput.addEventListener('input', () => {
    if (latestLobby) renderRooms(latestLobby.rooms);
    refreshCallsignWarn();
  });
  callsignInput.addEventListener('change', () => {
    saveStoredCallsign(currentName());
  });

  // View switching.
  const showView = (view: 'battles' | 'create'): void => {
    battlesView.hidden = view !== 'battles';
    createView.hidden = view !== 'create';
    scrollEl.scrollTop = 0;
    setBattlesStatus('');
    setCreateStatus('');
  };
  newBattleBtn.addEventListener('click', () => showView('create'));
  backBtn.addEventListener('click', () => showView('battles'));

  // Map picker.
  mapFilterInput.addEventListener('input', () => {
    if (latestLobby) renderMaps(latestLobby.maps);
  });
  mapsEl.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-map-name]');
    if (!btn) return;
    selectedMap = btn.dataset.mapName ?? selectedMap;
    if (latestLobby) renderMaps(latestLobby.maps);
  });

  // Game mode + hidden mines.
  modesEl.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-game-type]');
    if (!btn) return;
    gameType = Number(btn.dataset.gameType) as GameTypeCode;
    renderModes();
  });
  minesToggle.addEventListener('click', () => {
    hiddenMines = !hiddenMines;
    minesToggle.setAttribute('aria-pressed', String(hiddenMines));
  });

  // Bot grid: tap the tile to add one, the minus rail to remove one.
  botsEl.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const add = target.closest<HTMLElement>('[data-bot-add]');
    const minus = target.closest<HTMLElement>('[data-bot-minus]');
    const p = (add?.dataset.botAdd ?? minus?.dataset.botMinus) as BotPersonality | undefined;
    if (!p || !BOT_PERSONALITIES.includes(p)) return;
    const count = botCounts.get(p) ?? 0;
    if (add) botCounts.set(p, count + 1);
    else if (count > 0) botCounts.set(p, count - 1);
    renderBots();
  });

  // Create battle.
  createBtn.addEventListener('click', async () => {
    if (busy) return;
    if (!selectedMap) {
      setCreateStatus('// no map selected', true);
      return;
    }
    busy = true;
    createBtn.disabled = true;
    setCreateStatus(`// creating battle on "${selectedMap}"…`);
    try {
      const created = await createRoom(selectedMap, gameType, hiddenMines);
      navigateToBattle(created.id, botList());
    } catch (err) {
      setCreateStatus(`// create failed: ${String(err)}`, true);
      createBtn.disabled = false;
      busy = false;
    }
  });

  // ---- Kick off ---------------------------------------------------------------
  renderModes();
  renderBots();
  void refresh();
  startPolling();
}

// ---------------------------------------------------------------------------
// Static skeleton — dynamic regions are re-rendered by the functions above.
// ---------------------------------------------------------------------------

function renderSkeleton(): string {
  return `
    <div class="ml-scanlines" aria-hidden="true"></div>
    <div class="ml-scroll">
      <div class="ml-panel">
        <header class="ml-head">
          <div class="ml-brand">BOLO<em>2K</em></div>
          <div class="ml-readout" aria-live="polite">
            <span class="live"></span><span data-players>–</span> players · <span data-battles>–</span> battles
          </div>
          <div class="ml-tagline">// pick a battle — or <em>start a new one</em></div>
        </header>
        <div class="ml-hazard" aria-hidden="true"></div>

        <section class="ml-section">
          <h2><span class="ml-marker"></span>Your Callsign</h2>
          <div class="ml-field">
            <span class="ml-prefix">&gt;</span>
            <input type="text" data-callsign autocomplete="off" autocapitalize="off"
                   autocorrect="off" spellcheck="false" enterkeyhint="done"
                   placeholder="callsign" />
          </div>
          <p class="ml-callsign-warn" data-callsign-warn hidden>// callsign in use — auto-suffix on join</p>
        </section>

        <section class="ml-view" data-view="battles">
          <h2><span class="ml-marker"></span>Active Battles</h2>
          <p class="ml-help">Tap a battle to deploy.</p>
          <div class="ml-rooms" data-rooms>
            <div class="ml-loading">// contacting HQ…</div>
          </div>
          <div class="ml-status" data-battles-status></div>
          <button class="ml-btn ml-new" type="button" data-new-battle>+ Start New Battle</button>
        </section>

        <section class="ml-view" data-view="create" hidden>
          <button class="ml-btn ml-ghost ml-back" type="button" data-back>‹ Back to battles</button>

          <h2><span class="ml-marker"></span>Terrain</h2>
          <div class="ml-field ml-map-filter">
            <span class="ml-prefix">?</span>
            <input type="search" data-map-filter autocomplete="off" autocapitalize="off"
                   autocorrect="off" spellcheck="false" enterkeyhint="search"
                   placeholder="filter maps" />
          </div>
          <div class="ml-maps" data-maps>
            <div class="ml-loading">// loading maps…</div>
          </div>

          <h2><span class="ml-marker"></span>Game Mode</h2>
          <div class="ml-modes" data-modes></div>
          <p class="ml-mode-hint" data-mode-hint></p>
          <button class="ml-toggle" type="button" data-hidden-mines aria-pressed="true">
            <span>Hidden mines</span><span class="box" aria-hidden="true"></span>
          </button>

          <h2><span class="ml-marker"></span>Bots <span class="ml-optional">— optional</span></h2>
          <div class="ml-bots" data-bots></div>
          <p class="ml-bots-summary" data-bots-summary></p>

          <div class="ml-status" data-create-status></div>
          <button class="ml-btn ml-create" type="button" data-create>Create Battle</button>
        </section>

        <div class="ml-hazard" aria-hidden="true"></div>
      </div>
    </div>`;
}
